import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  IndustryTypeEnumType,
  InvestmentWishlistCompanyAggregatesEnumType,
} from '__generated__/graphql';
import { FC, useEffect } from 'react';

import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';

import industryToReadable from 'utils/enums/industry-to-readable';
import useQueryParams from 'utils/hooks/use-query-params';
import useScroll from 'utils/hooks/use-scroll';

const TAGGED_WISHLIST_COMPANIES_QUERY = gql(`
  query TaggedWishlistCompanies($filters: InvestmentWishlistCompanyAggregatesFiltersType, $limit: Int, $page: Int, $skip: Boolean) {
    wishlistCompanies(filters: $filters, limit: $limit, page: $page, skip: $skip) {
      nodes {
        count
        name
        website
        company {
          id
        }
      }
      pageInfo {
        hasNextPage
        totalCount
      }
    }
  }
`);

const TaggedWishlistCompaniesPage: FC = () => {
  const { onScroll, onComponentUnmount, onDataLoad, onReset } = useScroll({
    pageIdentifier: 'taggedInvestmentWishlistCompaniesScrollPosition',
  });
  const { industry, page, onLoadMore } = useQueryParams({
    industry: { param: 'industry', enumType: IndustryTypeEnumType },
  });

  const { loading, error, data, refetch, fetchMore, networkStatus, variables } = useQuery(
    TAGGED_WISHLIST_COMPANIES_QUERY,
    {
      variables: {
        limit: page * 15,
        page,
        skip: false,
        filters: {
          type: InvestmentWishlistCompanyAggregatesEnumType.Tagged,
          industry: industry as IndustryTypeEnumType | undefined,
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  useEffect(() => {
    if (data?.wishlistCompanies.nodes.length) onDataLoad();
  }, [data, onDataLoad]);

  const filters = [
    {
      type: 'industry',
      label: 'Industry',
      options: [
        {
          label: 'All',
          value: undefined,
        },
        ...Object.values(IndustryTypeEnumType).map(status => ({
          label: `${industryToReadable(status).label}`,
          value: status,
        })),
      ],
      readable: industryToReadable,
    },
  ];

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const wishlistCompanies = data.wishlistCompanies.nodes;
  const pageInfo = data.wishlistCompanies.pageInfo;

  return (
    <div className="max-w-3xl">
      <FilterPanel
        onFilterChange={onReset}
        activeFilters={variables?.filters}
        filters={filters}
        refetch={refetch}
      />
      <DataTable
        data={wishlistCompanies}
        onUnmount={onComponentUnmount}
        onScroll={onScroll}
        onClick={wishlistCompanyAggregate =>
          window.open(`/companies/${wishlistCompanyAggregate.company.id}/wishlisted`, '_blank')
        }
        columns={[
          {
            label: 'Company',
            fieldName: 'name',
          },
          {
            label: 'Website',
            fieldName: 'website',
            type: 'STRING',
          },
          {
            label: '# Times Wishlisted',
            fieldName: 'count',
            type: 'NUMBER',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        filterLoading={networkStatus === NetworkStatus.setVariables}
        paginationLoading={loading}
        onLoadMore={() => {
          onLoadMore();
          fetchMore({
            variables: {
              page: page + 1,
              limit: 15,
              skip: true,
            },
          });
        }}
      />
    </div>
  );
};

export default TaggedWishlistCompaniesPage;
